import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Receipt, FileDown } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { BillingFilters, TABS } from '../components/billing/BillingFilters';
import { BillingRow } from '../components/billing/BillingRow';

function fmtCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function BillingPage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [summary, setSummary] = useState({ ytdTotal: 0, ytdGst: 0 });

  const fetchRecords = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const params = { limit: 20 };
        if (activeTab.types && activeTab.types.length > 0) {
          params.type = activeTab.types.join(',');
        }
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (cursor) params.cursor = cursor;

        const res = await api.get('/billing/records', { params });
        const data = res.data || {};
        setRecords((prev) => (append ? [...prev, ...(data.records || [])] : data.records || []));
        setNextCursor(data.next_cursor || null);
        if (!append) {
          setSummary({
            ytdTotal: data.ytd_total_cents || 0,
            ytdGst: data.ytd_gst_cents || 0,
          });
        }
      } catch (err) {
        console.error('Failed to load billing records:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, activeTab, dateFrom, dateTo]
  );

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleDownload = async (record) => {
    try {
      const res = await api.get(`/billing/records/${record.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `WANTD-${record.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice:', err);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setNextCursor(null);
  };

  const handleDateChange = ({ from, to }) => {
    setDateFrom(from);
    setDateTo(to);
    setNextCursor(null);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          data-testid="billing-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center">
            <Receipt className="w-7 h-7 text-[#C9956C]" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
              Billing & invoices
            </h1>
            <p className="text-[var(--theme-text-secondary)] mt-1 text-sm">
              Tax invoices for subscriptions, boosts, purchases, and sale fees.
            </p>
          </div>
        </div>

        {(summary.ytdTotal > 0 || summary.ytdGst > 0) && (
          <div className="mb-6 p-4 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-xl flex items-center justify-between">
            <div>
              <div className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">
                Year to date
              </div>
              <div className="text-2xl font-bold text-[var(--theme-text-primary)] mt-1">
                {fmtCents(summary.ytdTotal)}
              </div>
            </div>
            {summary.ytdGst > 0 && (
              <div className="text-right">
                <div className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">
                  GST paid
                </div>
                <div className="text-lg font-semibold text-[var(--theme-text-primary)] mt-1">
                  {fmtCents(summary.ytdGst)}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <BillingFilters
            activeTab={activeTab.id}
            onTabChange={handleTabChange}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateChange={handleDateChange}
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#C9956C]" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12 text-[var(--theme-text-secondary)]">
            <FileDown className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No invoices yet.</p>
            <p className="text-sm mt-1">
              Chargeable events (subscriptions, boosts, purchases) will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {records.map((r) => (
              <BillingRow key={r.id} record={r} onDownload={handleDownload} />
            ))}
            {nextCursor && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => fetchRecords({ cursor: nextCursor, append: true })}
                  disabled={loadingMore}
                  data-testid="billing-load-more"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
